// pages/Register.tsx
import { useEffect, useState } from "react";
import { useLocation, Link } from "wouter";
import Layout from "@/components/Layout";
import SEO from '@/components/SEO'; // SEO: registration page -> noindex,nofollow
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { CheckCircle2, Clock, AlertCircle, Eye, EyeOff } from "lucide-react";
import { fetchJson } from '@/lib/fetchJson';
import { getOrCreateDeviceId } from '@/lib/deviceId';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;

// Simple password strength evaluator
function getPasswordStrength(pwd: string): '' | 'weak' | 'medium' | 'strong' {
  if (!pwd) return '';
  const lengthScore = pwd.length >= 12 ? 2 : pwd.length >= 8 ? 1 : 0; // still allow 6 as min, but reward 8+
  const variety = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].reduce((acc, r) => acc + (r.test(pwd) ? 1 : 0), 0);
  const score = lengthScore + variety; // range roughly 0-6
  if (pwd.length >= 6 && score >= 5) return 'strong';
  if (pwd.length >= 6 && score >= 3) return 'medium';
  return 'weak';
}

export default function Register() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  // useAuth ჰუკიდან სწორი მუტაციები
  const {
    sendCodeMutation,                // POST /api/auth/send-code
    verifyCodeMutation,              // POST /api/auth/verify-code  -> returns session_token
    completeRegistrationMutation,    // POST /api/auth/complete-registration (snake_case payload)
  } = useAuth();

  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
    verificationCode: "",
  });

  const [agreements, setAgreements] = useState({ terms: false, privacy: false, risk: false, refund: false });

  const [codeSent, setCodeSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [sessionToken, setSessionToken] = useState<string | null>(null);

  // ინლაინის შეცდომები
  const [emailError, setEmailError] = useState<string | null>(null);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [registrationStatus, setRegistrationStatus] = useState<'idle'|'success'|'error'>('idle');
  const [registrationErrorMsg, setRegistrationErrorMsg] = useState<string | null>(null);
  // New validation states
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [agreementsErrors, setAgreementsErrors] = useState<string[]>([]);

  // password visibility states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // resend cooldown
  const RESEND_SECONDS = 60;
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!cooldown) return;
    const t = setInterval(() => setCooldown((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  // როცა email იცვლება — ვერიფიკაცია/კოდი/შეცდომები განულდეს
  useEffect(() => {
    setCodeSent(false);
    setEmailVerified(false);
    setSessionToken(null);
    setCooldown(0);
    setEmailError(null);
    setVerificationError(null);
    setFormData((p) => ({ ...p, verificationCode: "" }));
  }, [formData.email]);

  // Helper to produce safe, user-facing errors (no server internals)
  function friendly(msg?: string, fallback = 'Something went wrong. Please try again.') {
    if (!msg) return fallback;
    const lower = msg.toLowerCase();
    if (/(rate limit|too many)/i.test(msg)) return 'Too many attempts. Please wait a moment before trying again.';
    if (/network|failed to fetch|timeout/i.test(msg)) return 'Network issue. Please check your connection and retry.';
    if (/(code|verification).*invalid|expired/i.test(lower)) return 'The verification code is invalid or expired.';
    if (/(session).*token/i.test(lower)) return fallback; // hide internals
    if (/(already|exist|used)/i.test(lower)) return 'This email is already in use.';
    if (/password.*match/i.test(lower)) return 'Passwords do not match.';
    if (/password.*short|at least/i.test(lower)) return 'Password is too short.';
    // default generic
    return fallback;
  }

  // კოდის გაგზავნა/გადაგზავნა
  const handleSendOrResendCode = async () => {
    if (!formData.email) {
      toast({ title: "Email Required", description: "Please enter your email address first.", variant: "destructive" });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({ title: "Invalid Email", description: "Please enter a valid email address.", variant: "destructive" });
      return;
    }

    try {
      setEmailError(null);
      await sendCodeMutation.mutateAsync(formData.email);
      setCodeSent(true);
      setCooldown(RESEND_SECONDS);
      toast({
        title: codeSent ? "Code Resent" : "Verification Code Sent",
        description: codeSent ? "We sent you a new code." : "Please check your email for the verification code.",
        className: "toast-success",
      });
    } catch (error: any) {
      const raw = String(error?.message || "");
      const emsg = raw.toLowerCase();
      if (emsg.includes("409") || emsg.includes("already") || emsg.includes("exist") || emsg.includes("used")) {
        setEmailError('This email is already in use. Try signing in or use a different email.');
      }
      toast({
        title: 'Error',
        description: friendly(raw, 'Failed to send verification code'),
        variant: 'destructive',
      });
    }
  };

  // კოდის ვერიფიკაცია — უნდა დააბრუნოს session_token
  const handleVerifyCode = async () => {
    if (!formData.verificationCode) {
      toast({ title: "Verification Required", description: "Please enter the verification code.", variant: "destructive" });
      return;
    }
    try {
      setVerificationError(null);
      const resp = await verifyCodeMutation.mutateAsync({ email: formData.email, code: formData.verificationCode });

      // მოველით resp.session_token-ს
      const tokenFromServer = resp?.session_token;
      if (!tokenFromServer) {
        throw new Error("Verification succeeded but no session token returned by server.");
      }

      setSessionToken(tokenFromServer);
      setEmailVerified(true);
      setCodeSent(false); // კოდის ველი დაიმალოს
      toast({ title: "Email Verified", description: "Great! You can now create your account.", className: "toast-success" });
    } catch (error: any) {
      const serverText = String(error?.message || "");
      const pretty = friendly(serverText, 'The verification code is invalid or has expired. Please try again.');
      setVerificationError(pretty);
      setEmailVerified(false);
      setSessionToken(null);
    }
  };

  // ფორმის ვალიდაცია (ღილაკის გასააქტიურებლად)

  const passwordStrength = getPasswordStrength(formData.password); // kept for visual meter
  const passwordTooShort = formData.password.length > 0 && formData.password.length < 6;
  const hasUppercase = /[A-Z]/.test(formData.password);
  const confirmMismatch =
    formData.confirmPassword.length > 0 && formData.confirmPassword !== formData.password;

  // New minimal rule: at least 6 chars AND at least one uppercase letter, plus match
  const passwordsOk =
    formData.password.length >= 6 && hasUppercase && formData.password === formData.confirmPassword;


  // ▶️ ღილაკი ჩაირთოს მხოლოდ მაშინ, როცა:
  //   - იმეილი ვერიფიცირებულია
  //   - გვაქვს session_token
  //   - ყველა ველი სავსეა
  //   - პაროლი >=6 სიმბოლო და შეიცავს ერთ დიდ ასოს (A-Z) და ემთხვევა დადასტურებას
  //   - ყველა შეთანხმება მონიშნულია
  //   - არ გვაქვს ინლაინის შეცდომები

  // Complete registration (NO auto-login; redirect to /login per spec)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);
    setAgreementsErrors([]);

    // Client-side validation even if button enabled
    let localEmailError: string | null = null;
    if (!formData.email.trim()) {
      localEmailError = 'Email is required.';
    } else if (!emailVerified) {
      localEmailError = 'Please verify your email before creating an account.';
    }
    if (localEmailError) setEmailError(localEmailError);
    else if (emailError && emailError.startsWith('Email is required')) setEmailError(null);

    const missingAgreements: string[] = [];
  if (!agreements.terms) missingAgreements.push('I must agree to the Terms of Use.');
  if (!agreements.privacy) missingAgreements.push('I must agree to the Privacy Policy.');
  if (!agreements.refund) missingAgreements.push('I must agree to the Refund Policy.');
  if (!agreements.risk) missingAgreements.push('I must acknowledge the Risk Disclosure.');
    if (missingAgreements.length) setAgreementsErrors(missingAgreements);

    // Passwords validation (reuse existing indicators) — no extra messages unless mismatch/too short logic already shows
    const basicValid = !localEmailError && missingAgreements.length === 0 && passwordsOk && !!sessionToken && !verificationError;
    if (!basicValid) {
      // Silent return: rely on red highlights only
      return;
    }

    setRegistrationStatus('idle');
    setRegistrationErrorMsg(null);
    try {
      await completeRegistrationMutation.mutateAsync({
        email: formData.email,
        sessionToken,
        firstName: formData.firstName,
        lastName: formData.lastName,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        acceptedTermsOfUse: agreements.terms,
        acceptedPrivacyPolicy: agreements.privacy,
        acceptedRefundPolicy: agreements.refund,
        acceptedRiskDisclosure: agreements.risk,
        legalAcceptanceMeta: {
          accepted_at: new Date().toISOString(),
          documents: [
            agreements.terms ? { slug: 'terms', accepted: true } : null,
            agreements.privacy ? { slug: 'privacy', accepted: true } : null,
            agreements.refund ? { slug: 'refund', accepted: true } : null,
            agreements.risk ? { slug: 'risk', accepted: true } : null,
          ].filter(Boolean)
        }
      });
      // Sync cookie_consent now: since user only agreed to legal policies here (no analytics/marketing opt-in), set both OFF.
      try {
        const device_id = getOrCreateDeviceId();
        await fetchJson(`${API_BASE_URL}/api/privacy/cookies/consent`, {
          method: 'POST',
          credentials: 'include',
          body: JSON.stringify({ action: 'manage', preferences: { necessary: true, analytics: false, marketing: false }, device_id }),
        });
        try { window.dispatchEvent(new CustomEvent('cookie-consent-changed', { detail: { necessary: true, analytics: false, marketing: false } })); } catch {}
      } catch {
        // non-fatal; banner logic will handle unauth flow
      }
      setRegistrationStatus('success');
      toast({
        title: 'Registration Successful',
        description: 'You have successfully registered. Please sign in.',
        className: 'toast-success'
      });
      try { localStorage.setItem('registrationSuccess','1'); } catch {}
      setTimeout(() => setLocation('/login'), 2000);
    } catch (error: any) {
      const raw = String(error?.message || '');
      setRegistrationStatus('error');
      const emsgLower = raw.toLowerCase();
      if (emsgLower.includes('409') || emsgLower.includes('already') || emsgLower.includes('exist') || emsgLower.includes('used')) {
        setEmailError('This email is already in use. Try signing in or use a different email.');
        setRegistrationErrorMsg('Email is already in use.');
      } else if (/password.*match/i.test(emsgLower)) {
        setRegistrationErrorMsg('Passwords do not match.');
      } else if (/password.*short|at least/i.test(emsgLower)) {
        setRegistrationErrorMsg('Password does not meet requirements.');
      } else {
        setRegistrationErrorMsg('Registration failed. Please try again.');
      }
      toast({
        title: 'Registration Failed',
        description: friendly(raw, 'Registration failed. Please try again.'),
        variant: 'destructive'
      });
    }
  };

  const isRegistering = completeRegistrationMutation.isPending;
  const isSending = sendCodeMutation.isPending;
  const isVerifying = verifyCodeMutation.isPending;

  return (
    <Layout>
      <SEO title="Create Account | TradeSagePro" description="Register to access AI-powered market research and analysis insights with a fair credit system and validated model metrics." canonical="https://tradesagepro.com/register" noindex nofollow />
      <div className="min-h-screen bg-[hsl(226,26%,7%)] text-[hsl(210,9%,91%)] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Card className="bg-[hsl(215,15%,18%)] border-gray-700 shadow-md">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl text-white">Create Account</CardTitle>
              <CardDescription className="text-[hsl(210,9%,80%)]">
                Join TradeSagePro and start analyzing smarter
              </CardDescription>
            </CardHeader>
            <CardContent>
              {registrationStatus === 'success' && (
                <div className="mb-4 rounded-md border border-green-500/40 bg-green-500/15 p-3 text-sm text-white flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-[2px] text-green-300" />
                  <div>
                    <p className="font-semibold">You have successfully registered.</p>
                    <p className="text-xs opacity-80">Redirecting you to Sign In...</p>
                  </div>
                </div>
              )}
              {registrationStatus === 'error' && (
                <div className="mb-4 rounded-md border border-red-500/50 bg-red-600/20 p-3 text-sm text-white flex items-start gap-2" role="alert">
                  <AlertCircle className="w-4 h-4 mt-[2px] text-red-300" />
                  <div>
                    <p className="font-semibold">Registration failed.</p>
                    <p className="text-xs opacity-80">{registrationErrorMsg || 'You were not registered. Please try again.'}</p>
                  </div>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4" aria-disabled={registrationStatus==='success'}>
                {/* Email + Send/Resend */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white">Email Address</Label>

                  <div className="relative flex gap-2">
                    {emailVerified && (
                      <CheckCircle2 className="absolute left-2 top-1/2 -translate-y-1/2 w-5 h-5 text-green-400 pointer-events-none" />
                    )}

                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Enter your email"
                      required
                      className={`flex-1 bg-[hsl(226,26%,7%)] text-white ${emailVerified ? 'pl-9' : ''} ${(emailError || (submitAttempted && (!formData.email || !emailVerified))) ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-600'}`}
                      disabled={isSending || isVerifying || emailVerified}
                      aria-invalid={!!emailError}
                      aria-describedby={emailError ? "email-error" : undefined}
                    />

                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleSendOrResendCode}
                      disabled={isSending || !formData.email || emailVerified || cooldown > 0}
                      className="shrink-0"
                    >
                      {emailVerified
                        ? "Verified"
                        : isSending
                          ? "Sending..."
                          : cooldown > 0
                            ? (<span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" />{cooldown}s</span>)
                            : codeSent
                              ? "Resend Code"
                              : "Send Code"}
                    </Button>
                  </div>

                  {(emailError || (submitAttempted && (!formData.email || !emailVerified))) && (
                    <div id="email-error" className="mt-2 flex items-start gap-2 text-white text-sm bg-red-600/20 border border-red-500/50 rounded px-3 py-2" role="alert" aria-live="polite">
                      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-red-300" />
                      <div>
                        <p className="font-medium">
                          {emailError ? emailError : (!formData.email ? 'Email is required.' : !emailVerified ? 'Please verify your email before creating an account.' : '')}
                        </p>
                        {emailError && emailError.includes('already') && (
                          <Link href="/login" className="underline text-red-200 hover:text-red-100">Go to Sign In</Link>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Verification input + შეცდომა */}
                  {codeSent && !emailVerified && (
                    <>
                      <div className="flex gap-2 mt-2">
                        <Input
                          id="verificationCode"
                          type="text"
                          value={formData.verificationCode}
                          onChange={(e) => {
                            setVerificationError(null);
                            setFormData({ ...formData, verificationCode: e.target.value });
                          }}
                          placeholder="Enter verification code"
                          required
                          className={`bg-[hsl(226,26%,7%)] text-white ${verificationError ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-gray-600"}`}
                          disabled={isVerifying}
                          aria-invalid={!!verificationError}
                          aria-describedby={verificationError ? "code-error" : undefined}
                        />
                        <Button
                          type="button"
                          onClick={handleVerifyCode}
                          disabled={isVerifying || !formData.verificationCode}
                          className="shrink-0"
                        >
                          {isVerifying ? "Verifying..." : "Verify"}
                        </Button>
                      </div>

                      {verificationError && (
                        <div id="code-error" className="mt-2 flex items-start gap-2 text-white text-sm bg-red-600/20 border border-red-500/50 rounded px-3 py-2" role="alert" aria-live="polite">
                          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-red-300" />
                          <span>{verificationError}</span>
                        </div>
                      )}

                      <p className="text-xs text-[hsl(210,9%,70%)] mt-1">
                        Didn’t get a code? {cooldown > 0 ? `You can resend in ${cooldown}s.` : "Click Resend Code."}
                      </p>
                    </>
                  )}

                  {emailVerified && (
                    <div className="flex items-center gap-2 text-green-400 text-sm mt-1">
                      <CheckCircle2 className="w-4 h-4" />
                      Email verified. You can proceed.
                    </div>
                  )}
                </div>

                {/* User details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName" className="text-white">First Name</Label>
                    <Input
                      id="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      placeholder="First name"
                      required
                      className="bg-[hsl(226,26%,7%)] border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName" className="text-white">Last Name</Label>
                    <Input
                      id="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      placeholder="Last name"
                      required
                      className="bg-[hsl(226,26%,7%)] border-gray-600 text-white"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password" className="text-white">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Create password (min. 6 characters)"
                      required
                      className={`pr-10 bg-[hsl(226,26%,7%)] text-white ${passwordTooShort ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-gray-600"}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(p => !p)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-[hsl(210,9%,70%)] hover:text-white focus:outline-none"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      <span className="sr-only">{showPassword ? "Hide" : "Show"} password</span>
                    </button>
                  </div>
                  {/* Strength meter */}
                  {formData.password.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 rounded bg-gray-700 overflow-hidden">
                          <div
                            className={`h-full transition-all duration-300 ${
                              passwordStrength === 'strong'
                                ? 'bg-green-500 w-full'
                                : passwordStrength === 'medium'
                                  ? 'bg-amber-400 w-2/3'
                                  : 'bg-red-500 w-1/3'
                            }`}
                          />
                        </div>
                        <span className={`text-xs ${
                          passwordStrength === 'strong'
                            ? 'text-green-400'
                            : passwordStrength === 'medium'
                              ? 'text-amber-300'
                              : 'text-red-400'
                        }`}>
                          {passwordStrength.charAt(0).toUpperCase() + passwordStrength.slice(1)}
                        </span>
                      </div>
                      {passwordTooShort && (
                        <p className="text-xs text-red-400">Password must be at least 6 characters.</p>
                      )}
                      {!passwordTooShort && formData.password.length > 0 && !hasUppercase && (
                        <p className="text-xs text-red-400">Password must contain at least one uppercase letter (A-Z).</p>
                      )}
                      {!passwordTooShort && hasUppercase && formData.password.length >= 6 && formData.password.length < 8 && (
                        <p className="text-xs text-[hsl(210,9%,70%)]">Optional: add more variety for a stronger password.</p>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-white">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder="Confirm password"
                      required
                      className={`pr-10 bg-[hsl(226,26%,7%)] text-white ${confirmMismatch ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-gray-600"}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(p => !p)}
                      aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-[hsl(210,9%,70%)] hover:text-white focus:outline-none"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      <span className="sr-only">{showConfirmPassword ? "Hide" : "Show"} confirm password</span>
                    </button>
                  </div>
                  {confirmMismatch && (
                    <p className="mt-1 text-xs text-red-400">Passwords do not match.</p>
                  )}
                </div>

                {/* შეთანხმებები */}
                <div className="space-y-3 text-sm text-[hsl(210,9%,85%)]">
                  <div className={`flex items-center space-x-2 ${submitAttempted && !agreements.terms ? 'text-red-400' : ''}`}>
                    <Checkbox id="terms" checked={agreements.terms} onCheckedChange={(c: boolean | "indeterminate") => setAgreements({ ...agreements, terms: !!c })} />
                    <Label htmlFor="terms" className={`text-sm ${submitAttempted && !agreements.terms ? 'text-red-400' : ''}`}>
                      I agree to the <Link href="/terms" className="text-[hsl(193,100%,50%)] hover:underline">Terms of Use</Link>
                    </Label>
                  </div>
                  <div className={`flex items-center space-x-2 ${submitAttempted && !agreements.privacy ? 'text-red-400' : ''}`}>
                    <Checkbox id="privacy" checked={agreements.privacy} onCheckedChange={(c: boolean | "indeterminate") => setAgreements({ ...agreements, privacy: !!c })} />
                    <Label htmlFor="privacy" className={`text-sm ${submitAttempted && !agreements.privacy ? 'text-red-400' : ''}`}>
                      I agree to the <Link href="/privacy" className="text-[hsl(193,100%,50%)] hover:underline">Privacy Policy</Link>
                    </Label>
                  </div>
                  <div className={`flex items-center space-x-2 ${submitAttempted && !agreements.refund ? 'text-red-400' : ''}`}>
                    <Checkbox id="refund" checked={agreements.refund} onCheckedChange={(c: boolean | "indeterminate") => setAgreements({ ...agreements, refund: !!c })} />
                    <Label htmlFor="refund" className={`text-sm ${submitAttempted && !agreements.refund ? 'text-red-400' : ''}`}>
                      I agree to the <Link href="/refund" className="text-[hsl(193,100%,50%)] hover:underline">Refund Policy</Link>
                    </Label>
                  </div>
                  <div className={`flex items-center space-x-2 ${submitAttempted && !agreements.risk ? 'text-red-400' : ''}`}>
                    <Checkbox id="risk" checked={agreements.risk} onCheckedChange={(c: boolean | "indeterminate") => setAgreements({ ...agreements, risk: !!c })} />
                    <Label htmlFor="risk" className={`text-sm ${submitAttempted && !agreements.risk ? 'text-red-400' : ''}`}>
                      I acknowledge the <Link href="/risk" className="text-[hsl(193,100%,50%)] hover:underline">Risk Disclosure</Link>
                    </Label>
                  </div>
                  {agreementsErrors.length > 0 && (
                    <div className="mt-2 text-xs bg-red-600/20 border border-red-500/50 rounded px-3 py-2 text-red-200 space-y-1" role="alert" aria-live="polite">
                      {agreementsErrors.map((er,i) => <div key={i}>• { er }</div>)}
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  className={`w-full bg-[hsl(193,100%,50%)] text-[hsl(226,26%,7%)] transition-all duration-300 hover:scale-105 ${isRegistering ? 'opacity-70 cursor-wait' : ''}`}
                  disabled={isRegistering}
                >
                  {isRegistering ? 'Creating Account...' : 'Create Account'}
                </Button>
              </form>

              <p className="text-center mt-4 text-sm text-[hsl(210,9%,70%)]">
                Already have an account?{" "}
                <Link href="/login" className="text-[hsl(193,100%,50%)] hover:underline">Sign in</Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
